"use client";

import { motion } from "framer-motion";
import { MessageSquare, ArrowRight, ShieldCheck } from "lucide-react";
import ExcursionNotice from "@/components/ExcursionNotice";
import ExcursionTerms from "@/components/ExcursionTerms";
import { excursionTerms, getExcursionWhatsAppUrl, getCairoWhatsAppUrl, getLuxorWhatsAppUrl } from "@/lib/excursionTerms";
import { excursionDetailHref } from "@/lib/sitePages";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
interface ExcursionItem {
  id: string;
  title: string;
  description: string;
  detailSlug: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
}
interface ExcursionCategory {
  id: string;
  title: string;
  subtitle: string;
  items: ExcursionItem[];
}
const excursionCategories: ExcursionCategory[] = [{
  id: "sea-island",
  title: "Meer & Inseln",
  subtitle: "Bootsfahrten, Korallenriffe und Inselaufenthalte im Roten Meer",
  items: [{
    id: "trip-orange-bay",
    title: "Orange Bay Insel Ausflug",
    description: "Genieße eine Bootsfahrt auf dem Roten Meer mit Entspannung am Sandstrand der Orange Bay und Schnorchelstopps. Zeitplan, Verpflegung und Schnorchelausrüstung werden im Angebot bestätigt.",
    detailSlug: "orange-bay-insel-ausflug",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/09/Orang.webp?v=qgf7hwu8jnj",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/09/Orang.webp?v=k8bykolsbbm-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/09/Orang.webp?v=dn5rz9sink-wvc-sizes",
    alt: "Orange Bay Insel Ausflug mit feinem Sandstrand und türkisblauem Wasser"
  }, {
    id: "trip-paradise-island",
    title: "Paradise Island Ausflug",
    description: "Verbinde Strandaufenthalt auf Paradise Island mit Bootsfahrt und Schnorchelstopps im offenen Meer. Aufenthaltsdauer, Mittagsverpflegung und Ausrüstung werden im Angebot bestätigt.",
    detailSlug: "paradise-island-ausflug",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/Paradise-Island-.webp?v=tw9g89gfzqi",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/Paradise-Island-.webp?v=b18b1p5z9pk-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/Paradise-Island-.webp?v=ljik2jwkx-wvc-sizes",
    alt: "Paradise Island Ausflug mit Sandstrand und Rotem Meer"
  }, {
    id: "trip-private-speed-boat",
    title: "Private Speedboot Tour",
    description: "Chartere ein privates Speedboot exklusiv für dich und deine Gruppe. Flexibel anpassbar nach Ziel, Dauer und Wünschen; Route, Stopps und Preis werden in deinem Angebot bestätigt.",
    detailSlug: "private-speedboot-tour",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/Speed.jpeg?v=zgjkspbd8e9",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/Speed.jpeg?v=fttkn4612lb-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/Speed.jpeg?v=e0q9najbebg-wvc-sizes",
    alt: "Private Speedboot Tour exklusiv für dich und deine Gruppe auf dem Roten Meer"
  }, {
    id: "trip-submarine",
    title: "Sea Scope Halb-U-Boot",
    description: "Beobachte die Riffe und Meeresbewohner durch große Unterwasser-Panoramafenster, während das Boot an der Wasseroberfläche bleibt. Mögliche Schnorchelstopps werden im Angebot bestätigt.",
    detailSlug: "sea-scope-ausflug-im-roten-meer",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/09/80.jpg?v=178bubs0dl2",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/09/80.jpg?v=7ilq3p8w7go-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/09/80.jpg?v=fc2vl82wcv5-wvc-sizes",
    alt: "Sea Scope Halb-U-Boot Unterwasser-Panoramafenster mit Korallenriffblick"
  }, {
    id: "trip-scuba-diving",
    title: "Tauchausflug mit dem Boot",
    description: "Entdecke die Unterwasserwelt bei einem geführten Tauchboot-Ausflug. Geeignet für zertifizierte Taucher sowie Schnuppertauchgänge; Tauchgänge, Tiefen und Ausrüstung werden im Angebot bestätigt.",
    detailSlug: "tauchausflug-mit-dem-boot",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_8675-scaled.jpeg?v=710qzryx6c4",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_8675-scaled.jpeg?v=7nxo3oncq4-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_8675-scaled.jpeg?v=r7x0aq8j0rm-wvc-sizes",
    alt: "Tauchausflug mit dem Boot im Roten Meer vor Hurghada"
  }, {
    id: "trip-parasailing",
    title: "Parasailing in Hurghada",
    description: "Gleite mit dem Fallschirm über die Küste des Roten Meeres – als Einzel- oder Tandemflug mit vollständiger Sicherheitsausrüstung. Flugdauer und Kombinationen werden im Angebot bestätigt.",
    detailSlug: "parasailing-in-hurghada",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_8821-scaled.jpeg?v=ekhtz0ksn8t",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_8814-scaled.jpeg?v=2oz4n3smdiv-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_8814-scaled.jpeg?v=0zdjj04xf9zr-wvc-sizes",
    alt: "Parasailing in Hurghada über dem Roten Meer"
  }]
}, {
  id: "desert-adventure",
  title: "Wüstenabenteuer & Reitausflüge",
  subtitle: "Fahrten mit Quad und Buggy, Wüstentouren und geführte Ausritte",
  items: [{
    id: "trip-desert-safari",
    title: "Super Safari Abenteuer",
    description: "Erlebe die Wüste rund um Hurghada mit einem abwechslungsreichen Offroad-Programm. Fahrabschnitte mit Quad oder Buggy, Beduinencamp-Besuch, Abendessen und Show werden im Angebot bestätigt.",
    detailSlug: "super-safari-abenteuer",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_8983-scaled.jpeg?v=vacrzs8fi4",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_8983-scaled.jpeg?v=2q7qik8xhoy-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_8983-scaled.jpeg?v=6akjxkeo0ht-wvc-sizes",
    alt: "Super Safari Abenteuer durch die Wüstenlandschaft bei Hurghada"
  }, {
    id: "trip-buggy-safari",
    title: "Buggy Safari",
    description: "Fahre mit einem geländegängigen Wüsten-Buggy über offene Sandpisten. Die genaue Fahrtzeit, Route, Fahrzeugbelegung und Pausenstopps werden vor deiner Buchung im Angebot bestätigt.",
    detailSlug: "buggy-safari-2",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/pexels-koto-28932608-636x426-1.webp?v=ruhbrrzpdp",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/pexels-koto-28932608-636x426-1.webp?v=sxli644nbc9-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/pexels-koto-28932608-636x426-1.webp?v=1r5dd84vsxn-wvc-sizes",
    alt: "Buggy Safari durch die Wüstenlandschaft bei Hurghada"
  }, {
    id: "trip-quad-safari",
    title: "Quad Bike Safari in Hurghada",
    description: "Geführte Tour mit vierrädrigen Quads über abgesteckte Pisten durch die Wüste rund um Hurghada inklusive Einweisung. Fahrzeit, Streckenführung und Pausen werden im Angebot bestätigt.",
    detailSlug: "quad-safari-in-hurghada",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/DSC_0015.JPG-scaled.jpeg?v=xo4j4pcfmi",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/DSC_0015.JPG-scaled.jpeg?v=qj4gcxpl59b-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/DSC_0015.JPG-scaled.jpeg?v=neyzu0nf32-wvc-sizes",
    alt: "Quad Bike Safari in Hurghada über offene Wüstenpisten"
  }, {
    id: "trip-horse-riding",
    title: "Reitausflug in Hurghada",
    description: "Geführter Ausritt durch Wüsten- und Küstenabschnitte mit erfahrenen Pferdebetreuern. Streckenverlauf, Reitzeit und individuelle Teilnahmevoraussetzungen werden im Angebot bestätigt.",
    detailSlug: "reitausflug-in-hurghada",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_9002-scaled.jpeg?v=mcf9fs3jmgj",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_9002-scaled.jpeg?v=nkyzvabvze8-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_9002-scaled.jpeg?v=uaxfdq5kvl-wvc-sizes",
    alt: "Reitausflug in Hurghada durch Wüste und Küstengebiete"
  }, {
    id: "trip-horse-riding-sea-desert",
    title: "Reitausflug zwischen Meer und Wüstenlandschaft",
    description: "Abwechslungsreicher Ausritt entlang der Küste und durch die Wüstenlandschaft. Die genaue Reitroute, Dauer und Anforderungen für Reiter werden vorab im Angebot bestätigt.",
    detailSlug: "reitausflug-zwischen-meer-und-wustenlandschaft",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/e3.jpg?v=mg1a10hn25d",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/e3.jpg?v=bxl8cndub3m-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/e3.jpg?v=8g9e2dy9yzy-wvc-sizes",
    alt: "Reitausflug zwischen Meer und Wüstenlandschaft bei Hurghada"
  }]
}, {
  id: "culture-daytrips",
  title: "Kultur & Sehenswürdigkeiten",
  subtitle: "Historische Weltwunder, antike Tempelanlagen und kulturelle Höhepunkte",
  items: [{
    id: "trip-cairo",
    title: "Private Kairo-Tour ab Hurghada",
    description: "Vollständig privater Tagesausflug mit privatem Straßentransport und privater Reiseleitung exklusiv für deine Gruppe. Pyramiden von Gizeh, Große Sphinx, Grand Egyptian Museum (GEM) und Mittagessen inklusive.",
    detailSlug: "tagesausflug-nach-kairo-zu-den-pyramiden",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/09/cairo_luxor_tours.webp?v=tybpayqyemj",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/09/cairo_luxor_tours.webp?v=dls2ho3ujk6-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/09/cairo_luxor_tours.webp?v=un8l8do8eqj-wvc-sizes",
    alt: "Private Kairo-Tour ab Hurghada mit Pyramiden von Gizeh und Sphinx"
  }, {
    id: "trip-luxor",
    title: "Private Luxor-Tour ab Hurghada",
    description: "Vollständig privater Tagesausflug mit privatem Straßentransport und privater Reiseleitung exklusiv für deine Gruppe. Karnak-Tempel, Tal der Könige (drei Gräber), Hatschepsut-Tempel, Memnonkolosse und Mittagessen inklusive.",
    detailSlug: "tagesausflug-nach-luxor",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1810038/img/luxor_temple_excursion.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1810038/img/luxor_temple_excursion.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1810038/img/luxor_temple_excursion.png-wvc-sizes",
    alt: "Private Luxor-Tour ab Hurghada mit Karnak-Tempelanlage"
  }, {
    id: "trip-hurghada-city-tour",
    title: "Stadtrundfahrt durch Hurghada",
    description: "Geführte Entdeckungstour zu ausgewählten Stationen in Hurghada, darunter Marina und Altstadt-Aussichtspunkte. Einzelne Besichtigungen, Stationen und Dauer werden im Angebot bestätigt.",
    detailSlug: "stadtrundfahrt-durch-hurghada",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/Hurghada-Highlights-City-Tour-1.webp?v=oc607f2tqe",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/Hurghada-Highlights-City-Tour-1.webp?v=e9c0l68jaqk-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/Hurghada-Highlights-City-Tour-1.webp?v=a8h9fvg14ub-wvc-sizes",
    alt: "Stadtrundfahrt durch Hurghada mit Marina und Altstadtblick"
  }, {
    id: "trip-mini-egypt-park",
    title: "Ausflug zum Mini Egypt Park",
    description: "Besuche das Freilichtmuseum mit detailgetreuen Miniatur-Nachbildungen berühmter ägyptischer Baudenkmäler. Aufenthaltsdauer und Eintrittsdetails werden vorab im Angebot bestätigt.",
    detailSlug: "ausflug-zum-mini-egypt-park",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/images.jpeg?v=uldcqnjlr2",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/images.jpeg?v=nehwjsxfrm-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/images.jpeg?v=3y1wnrs7hn9-wvc-sizes",
    alt: "Ausflug zum Mini Egypt Park mit Miniaturbauwerken historischer Stätten"
  }]
}, {
  id: "shows-experiences",
  title: "Shows & Erlebnisse",
  subtitle: "Unterhaltung und besondere Begegnungen für die ganze Familie",
  items: [{
    id: "trip-dolphin-world-show",
    title: "Delfinshow",
    description: "Erlebe eine abwechslungsreiche Vorführung mit Meeressäugern. Beginnzeiten und Sitzplätze werden im Angebot bestätigt. Delfinschwimmen und Fotopakete sind separat buchbar.",
    detailSlug: "dolphin-world-delfinshow",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/unnamed-1.webp?v=0ly68yukssb",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/unnamed-1.webp?v=n34ael618i-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/unnamed-1.webp?v=68iscvmz3n5-wvc-sizes",
    alt: "Delfinshow mit Vorführung von Meeressäugern"
  }, {
    id: "trip-dolphin-world-swimming",
    title: "Delfinschwimmen bei Dolphin World",
    description: "Betreute Schwimmeinheit mit Delfinen im Poolbereich bei Dolphin World. Zeitdauer und Teilnahmebedingungen werden vor der Buchung bestätigt; Showeintritt und Fotos sind separat.",
    detailSlug: "delfinschwimmen-bei-dolphin-world",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/unnamed-2.webp?v=zkiab8at9k",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/unnamed-1.webp?v=4l6fyrq6d9v-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/unnamed-1.webp?v=qggrl8pifb-wvc-sizes",
    alt: "Delfinschwimmen bei Dolphin World im betreuten Beckenbereich"
  }]
}];
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.06
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 18
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: "easeOut"
    }
  }
};
export default function Excursions() {
  const buildExcursionWhatsAppUrl = (trip: ExcursionItem) => {
    if (trip.id === "trip-cairo") {
      return getCairoWhatsAppUrl("de");
    }
    if (trip.id === "trip-luxor") {
      return getLuxorWhatsAppUrl("de");
    }
    return getExcursionWhatsAppUrl("de", trip.title);
  };
  const whatsappGeneralUrl = "https://wa.me/201010634627";
  return <section id="day-trip-excursions" data-nav="light" className="relative bg-background text-foreground py-16 md:py-20 border-b border-border">
      <div className="mx-auto max-w-[1136px] px-6">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="flex flex-col items-center text-center max-w-3xl mx-auto mb-14 md:mb-16">
          <span className="text-xs uppercase tracking-wider font-semibold text-primary mb-3">
            Ausflugskatalog Ägypten
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-[2.6rem] font-bold tracking-tight text-foreground leading-tight">
            Ausflüge & Touren ab Hurghada
          </h2>

          {/* Signature Hairline Rule */}
          <div className="flex items-center justify-center my-4 w-32" aria-hidden="true">
            <div className="h-[1px] bg-border flex-1" />
            <div className="size-2 rounded-full bg-primary mx-1" />
            <div className="h-[1px] bg-border flex-1" />
          </div>

          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Entdecke unsere 17 Ausflüge rund um Hurghada und darüber hinaus. Dein Hoteltransfer ist privat; die Aktivität findet je nach gebuchtem Angebot in einer Gruppe oder privat statt. Wähle deinen Ausflug und frage Verfügbarkeit und Preis direkt per WhatsApp an.
          </p>
        </motion.div>

        {/* 4 Categorized Excursion Sections */}
        <div className="space-y-16 md:space-y-20">
          {excursionCategories.map(category => <div key={category.id} className="space-y-6 md:space-y-8">
              {/* Category Header */}
              <div className="border-b border-border pb-4">
                <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-1">
                  <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
                    {category.title}
                  </h3>
                  <span className="text-xs md:text-sm font-medium text-muted-foreground">
                    {category.subtitle}
                  </span>
                </div>
              </div>

              {/* Cards Grid */}
              <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
            once: true,
            amount: 0.08
          }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.items.map((trip, i) => {
              const whatsappLink = buildExcursionWhatsAppUrl(trip);
              return <motion.div key={trip.id} data-index={i} variants={cardVariants} className="flex min-w-0">
                      <Card className="flex flex-col justify-between w-full overflow-hidden border border-border bg-card rounded-md shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/50 hover:shadow-md min-w-0">
                        <div className="min-w-0">
                          {/* Image Container with Badge Overlay Wrapped in Link */}
                          <Link href={excursionDetailHref(trip.detailSlug)} aria-label={trip.title} className="block group/image">
                            <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
                              <img src={trip.image} data-wvc-srcset={trip.srcset} data-wvc-sizes={trip.sizes} alt={trip.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover/image:scale-105" />

                              {/* Uniform Badge Overlay */}
                              <div className="absolute top-3 left-3 right-3 flex flex-wrap gap-2 pointer-events-none">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider bg-black/75 text-white backdrop-blur-xs shadow-xs">
                                  <ShieldCheck className="size-3.5 text-[#27BA2A]" />
                                  Privater Hoteltransfer inklusive
                                </span>
                              </div>
                            </div>
                          </Link>

                          <CardContent className="pt-5 pb-4 px-6 flex flex-col gap-3 min-w-0">
                            <Link href={excursionDetailHref(trip.detailSlug)} className="hover:text-primary transition-colors block">
                              <h4 className="text-xl font-bold tracking-tight text-card-foreground hover:text-primary transition-colors whitespace-normal break-words">
                                {trip.title}
                              </h4>
                            </Link>

                            <p className="text-sm leading-relaxed text-muted-foreground whitespace-normal break-words">
                              {trip.description}
                            </p>
                          </CardContent>
                        </div>

                        {/* Card Footer with Single WhatsApp Quotation Button */}
                        <div className="px-6 pb-6 pt-3 mt-auto border-t border-border flex flex-col gap-3 bg-card">
                          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                            <Button asChild variant="outline" size="sm" className="w-full sm:w-1/2 h-9 px-3 rounded-md text-xs font-semibold border-border hover:bg-accent hover:text-accent-foreground transition-colors shadow-xs">
                              <Link href={excursionDetailHref(trip.detailSlug)} aria-label={trip.title}>
                                Details anzeigen
                                <ArrowRight className="size-3 ml-1.5 shrink-0" />
                              </Link>
                            </Button>

                            <Button asChild size="sm" className="w-full sm:w-1/2 h-9 px-3 rounded-md text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-xs text-center whitespace-normal break-words">
                              <Link to={whatsappLink} newTab>
                                <MessageSquare className="size-3.5 mr-1 shrink-0" />
                                <span className="truncate">{excursionTerms.de.priceOnWhatsApp}</span>
                              </Link>
                            </Button>
                          </div>
                        </div>
                      </Card>
                    </motion.div>;
            })}
              </motion.div>
            </div>)}
        </div>

        {/* Private Transfer Explanation Info Box */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45
      }} className="mt-14">
          <ExcursionNotice lang="de" />
        </motion.div>

        {/* Excursion Terms Component */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45
      }} className="mt-6">
          <ExcursionTerms lang="de" variant="full" />
        </motion.div>

        {/* Final WhatsApp Enquiry Section */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        delay: 0.08
      }} className="mt-10 flex flex-col items-center justify-center p-8 bg-secondary rounded-md border border-border text-center">
          <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2 max-w-xl">
            Bereit für deinen Ausflug?
          </h3>
          <p className="text-sm md:text-base text-muted-foreground mb-6 max-w-xl leading-relaxed">
            Sende uns deinen gewünschten Ausflug, den Hotelnamen, dein Wunschdatum, die Anzahl der Erwachsenen und das Alter der Kinder. Wir bestätigen Verfügbarkeit, Programm, enthaltene Leistungen und Preis vor der Buchung.
          </p>

          <Button asChild size="lg" className="rounded-md bg-[#27BA2A] text-white font-semibold shadow-xs hover:bg-[#22a025] transition-colors px-7 py-3">
            <Link to={whatsappGeneralUrl} newTab>
              <MessageSquare className="size-4 mr-2" />
              Jetzt per WhatsApp anfragen
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}